import React, { useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { userPossessionHeart } from 'src/lib/message'
import { authActionTypes, useAuthDispatch, useAuthState } from 'src/providers/AuthProviders'
import { useDuringContext } from './_Provider'
import { postGiveHeartItem } from 'src/services/post/postGiveHeartItem'
import { patchSetHeartChecking } from 'src/services/patch/patchSetHeartChecking'
import { ShopModal } from './_ShopModal'
import { HeartSupportModal } from './_HeartSupportModal'
import { AddHeartItemModal } from './_AddHeartItemModal'
import { InsufficientDiamondForHeartModal } from './_InsufficientDiamondForHeartModal'

import HeartItemAddition from 'src/pages/voiceLive/assets/heart_item_addition.png'
import FloatingHeartPurple from 'src/pages/voiceLive/assets/floating_heart_purple.gif'
import FloatingHeartGreen from 'src/pages/voiceLive/assets/floating_heart_green.gif'
import FloatingHeartRed from 'src/pages/voiceLive/assets/floating_heart_red.gif'

const heartGifs = { purple: FloatingHeartPurple, green: FloatingHeartGreen, red: FloatingHeartRed }

const FloatingHeartModal = () => {
  const { user } = useAuthState()
  const authDispatch = useAuthDispatch()

  const [heartSupportModal, setHeartSupportModal] = useState(false)
  const [heartItemModal, setHeartItemModal] = useState(false)
  const [confirmModal, setConfirmModal] = useState(false)
  const [insufficientDiamond, setInsufficientDiamond] = useState(false)
  const [isFirstTime, setIsFirstTime] = useState(true)
  const [isDisabled, setIsDisabled] = useState(false)
  const [hearts, setHearts] = useState([])
  const [userHeart, setUserHeart] = useState(0)
  const timeoutRefs = useRef([])

  const { liveItemsSocket, liveInfoQuery } = useDuringContext()
  const livestreamId = useMemo(() => liveInfoQuery.data?.live_stream?.id, [liveInfoQuery])

  const addHeart = (color) => {
    const newHeart = {
      id: uuidv4(),
      gif: heartGifs[color],
      cacheBuster: Math.random()
    }
    setHearts((prev) => [...prev, newHeart])
    const removeTimeout = setTimeout(() => {
      setHearts((prev) => prev.filter((gif) => gif.id !== newHeart.id))
      timeoutRefs.current = timeoutRefs.current.filter((t) => t !== removeTimeout)
    }, 1120)
    timeoutRefs.current.push(removeTimeout)
  }

  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach(clearTimeout)
    }
  }, [])

  useEffect(() => {
    setUserHeart(userPossessionHeart(user))
  }, [user])

  useEffect(() => {
    if (liveInfoQuery.data) {
      setIsFirstTime(!liveInfoQuery.data.is_heart_checking)
    }
  }, [liveInfoQuery.data])

  useEffect(() => {
    if (liveItemsSocket.item && liveItemsSocket.item.type === 'heart') {
      addHeart(liveItemsSocket.item.old_color)
    }
  }, [liveItemsSocket.item])

  const openLiveModal = async () => {
    if (isDisabled) return

    if (isFirstTime) {
      await patchSetHeartChecking({
        livestream_id: livestreamId
      })
      setIsFirstTime(false)
      setHeartSupportModal(true)
    } else if (userHeart < 1) {
      setInsufficientDiamond(true)
    } else {
      setHeartItemModal(true)
    }
  }

  const handleHeartClick = async () => {
    const res = await postGiveHeartItem({
      livestream_id: livestreamId
    })
    if (res.success) {
      setIsDisabled(true)
      const disableTimeout = setTimeout(() => setIsDisabled(false), 500)
      timeoutRefs.current.push(disableTimeout)

      authDispatch({
        type: authActionTypes.SPEND_HEART,
        payload: 1
      })
    }
  }

  return (
    <div className="relative flex flex-col items-center justify-center z-[9] mb-2">
      {hearts.length > 0 && (
        <div className="absolute -top-[180px] left-1/2 transform -translate-x-1/2 w-[115px]">
          {hearts.map((heart) => (
            <img
              key={heart.id}
              src={`${heart.gif}?cb=${heart.cacheBuster}`}
              alt="heart"
              className="absolute top-0 w-full object-contain"
            />
          ))}
        </div>
      )}
      <img
        src={HeartItemAddition}
        className={`w-[52px] h-auto transition-opacity ${
          isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
        onClick={() => openLiveModal()}
      />

      <ShopModal open={confirmModal} setOpen={setConfirmModal} />

      <HeartSupportModal open={heartSupportModal} onClose={() => setHeartSupportModal(false)} />

      <AddHeartItemModal
        open={heartItemModal}
        onClose={() => setHeartItemModal(false)}
        possessionHeart={userHeart}
        onSubmit={() => {
          setHeartItemModal(false)
          handleHeartClick()
        }}
      />

      <InsufficientDiamondForHeartModal
        open={insufficientDiamond}
        onClose={() => setInsufficientDiamond(false)}
      />
    </div>
  )
}

export { FloatingHeartModal }
